/**
 * HTML- UND SVG-FILTER — was Nutzer als Text oder Vektorgrafik hinterlegen,
 * geht nicht mehr ungeprueft in die Seite.
 *
 * ANLASS (Jans Frage 02.10.2026: „Kann dort jemand Dinge einsetzen, die
 * negative Auswirkungen haben koennen?"): Der Text einer Slide (und Credits,
 * Quellen, Kontakt) wurde als rohes HTML in die Seite geschrieben, eine
 * Vektor-Ebene als rohes SVG — ohne Filter und ohne Content-Security-Policy.
 * Gemessen mit harmlosen Proben: `<img onerror>`, `<details ontoggle>` und
 * `<iframe srcdoc>` fuehrten Code in UNSERER Seite aus; der Viewer liefert den
 * Slide-Text schon vom Server in die Seite, dort liefe sogar `<script>`. Wer
 * ein Konto hat (Registrierung ist offen), haette damit das Login eines
 * angemeldeten Lesers auslesen koennen — der Viewer haelt es im
 * sessionStorage.
 *
 * REGEL: Erlaubnisliste statt Verbotsliste. Es bleibt, was Autoren brauchen —
 * Textformatierung, Links, Bilder, Videos, Stile (auch `<style>`-Bloecke, die
 * Kunden-Scrollys nutzen) und eingebettete Seiten. Es faellt weg, was Code
 * ausfuehrt oder Daten abgreift: Skripte, alle `on…`-Attribute,
 * `javascript:`-Adressen, `srcdoc`, Formulare und Eingabefelder
 * (Anmelde-Attrappen), `<object>`/`<embed>`.
 *
 * EINGEBETTETE SEITEN (`<iframe>`) bleiben moeglich, aber eingehegt:
 *   • nur mit echter Webadresse (http/https);
 *   • immer mit `sandbox` — die fremde Seite darf Skripte, Formulare und neue
 *     Tabs, aber NICHT den ganzen Tab auf eine andere Adresse umleiten;
 *   • `allow` nur fuer Unbedenkliches (Vollbild, Autoplay …) — Kamera,
 *     Mikrofon und Standort lassen sich nicht an fremde Seiten durchreichen.
 *
 * ZWEI WERKZEUGE, EIN REGELSATZ (htmlFilterRegeln.ts):
 *   • Im Browser filtert DOMPurify mit dem echten Dokument.
 *   • Der Viewer rendert Slide-Texte schon auf dem Server — dort gibt es kein
 *     Dokument. Der Server meldet deshalb seinen eigenen Filter an
 *     (`setzeServerHtmlFilter`, s. htmlFilterServer.ts und
 *     public/src/lib/html-filter-server.ts).
 * Ist auf dem Server KEIN Filter angemeldet, wird NICHTS durchgelassen: Der
 * Text kommt ohne jedes HTML an — lieber unformatiert als ungefiltert.
 */
import createDOMPurify from 'dompurify';
import {
  IFRAME_REFERRER,
  IFRAME_SANDBOX,
  NUR_WEBADRESSE,
  VERBOTENE_ATTRIBUTE,
  VERBOTENE_TAGS,
  ZUSATZ_ATTRIBUTE,
  ZUSATZ_TAGS,
  entschaerfeStilBloecke,
  gefiltertesAllow,
  istEigeneDomain,
  nurText,
  sicheresRel,
} from './htmlFilterRegeln';

export { IFRAME_SANDBOX } from './htmlFilterRegeln';

type Reiniger = ReturnType<typeof createDOMPurify>;

const HTML_KONFIG = {
  USE_PROFILES: { html: true },
  ADD_TAGS: ZUSATZ_TAGS,
  ADD_ATTR: ZUSATZ_ATTRIBUTE,
  FORBID_TAGS: VERBOTENE_TAGS,
  FORBID_ATTR: VERBOTENE_ATTRIBUTE,
  // Sonst wandert ein `<style>` am Anfang des Texts in den <head> und geht
  // beim Auslesen verloren.
  FORCE_BODY: true,
};

const SVG_KONFIG = {
  USE_PROFILES: { svg: true, svgFilters: true },
  // `<use>` ist bei DOMPurify aus (kann fremde Dateien nachladen). Exporte
  // aus Illustrator/Figma brauchen es fuer Symbole — erlaubt, aber nur mit
  // Verweis INNERHALB der Datei (s. Haken unten).
  ADD_TAGS: ['use'],
};

const imBrowser = (): boolean =>
  typeof window !== 'undefined' && typeof window.document !== 'undefined';

function baueHtmlReiniger(): Reiniger {
  const reiniger = createDOMPurify(window);
  reiniger.addHook('afterSanitizeAttributes', (element) => {
    const name = element.nodeName;
    if (name === 'IFRAME') {
      const adresse = (element.getAttribute('src') ?? '').trim();
      if (!NUR_WEBADRESSE.test(adresse) || istEigeneDomain(adresse)) {
        element.remove();
        return;
      }
      element.setAttribute('sandbox', IFRAME_SANDBOX);
      if (!element.hasAttribute('referrerpolicy')) {
        element.setAttribute('referrerpolicy', IFRAME_REFERRER);
      }
      const allow = element.getAttribute('allow');
      if (allow !== null) {
        const bleibt = gefiltertesAllow(allow);
        if (bleibt !== null) element.setAttribute('allow', bleibt);
        else element.removeAttribute('allow');
      }
      return;
    }
    if (name === 'A' && element.hasAttribute('target')) {
      element.setAttribute('rel', sicheresRel(element.getAttribute('rel')));
    }
  });
  return reiniger;
}

function baueSvgReiniger(): Reiniger {
  const reiniger = createDOMPurify(window);
  reiniger.addHook('afterSanitizeAttributes', (element) => {
    if (element.nodeName.toLowerCase() !== 'use') return;
    const verweis = (
      element.getAttribute('href') ??
      element.getAttribute('xlink:href') ??
      ''
    ).trim();
    if (!verweis.startsWith('#')) element.remove();
  });
  return reiniger;
}

let browserReiniger: { html: Reiniger; svg: Reiniger } | null | undefined;

/** DOMPurify mit dem echten Dokument — `null` ausserhalb eines Browsers. */
function imBrowserFiltern(): { html: Reiniger; svg: Reiniger } | null {
  if (browserReiniger === undefined) {
    browserReiniger = null;
    if (imBrowser()) {
      const html = baueHtmlReiniger();
      if (html.isSupported) browserReiniger = { html, svg: baueSvgReiniger() };
    }
  }
  return browserReiniger;
}

/**
 * Der Filter des Servers. Liegt an `globalThis`, nicht im Modul: Im
 * Server-Paket des Viewers kann dieses Modul mehrfach vorkommen — alle Kopien
 * sollen denselben Filter benutzen.
 */
type ServerHtmlFilter = (html: string) => string;
const SERVER_SCHLUESSEL = '__scrollyServerHtmlFilter';

function serverHtmlFilter(): ServerHtmlFilter | undefined {
  return (globalThis as Record<string, unknown>)[SERVER_SCHLUESSEL] as
    | ServerHtmlFilter
    | undefined;
}

/**
 * Den HTML-Filter fuer den Server anmelden (oder mit `undefined` abmelden).
 * Der Viewer-Server ruft das einmal beim Start. Im Browser hat das keine
 * Wirkung — dort filtert immer DOMPurify.
 */
export function setzeServerHtmlFilter(
  filter: ServerHtmlFilter | undefined,
): void {
  (globalThis as Record<string, unknown>)[SERVER_SCHLUESSEL] = filter;
  leereSpeicher();
}

/** Der Lauf, der hier und jetzt HTML filtert — oder keiner. */
function htmlLauf(): ServerHtmlFilter | undefined {
  const browser = imBrowserFiltern();
  if (browser) {
    return (html) =>
      browser.html.sanitize(entschaerfeStilBloecke(html), HTML_KONFIG);
  }
  if (imBrowser()) return undefined;
  return serverHtmlFilter();
}

/** Kann hier gefiltert werden? (Fuer Tests und die Start-Pruefung des Servers.) */
export function htmlFilterBereit(): boolean {
  return htmlLauf() !== undefined;
}

/**
 * Derselbe Text wird sehr oft gefiltert: Der Player rendert ihn immer wieder,
 * und der Viewer-Server baut dieselbe Seite fuer jeden Abruf neu. Die
 * Ergebnisse bleiben deshalb liegen (zuletzt Benutztes bleibt am laengsten).
 *
 * Die Grenze ist eine ZEICHEN-Menge, keine Anzahl: Sonst koennte jemand den
 * Speicher des Servers mit wenigen, sehr langen Texten fuellen. 4 Mio. Zeichen
 * (Eingabe + Ergebnis) fassen den gesamten oeffentlichen Bestand mehrfach
 * (gemessen 02.10.2026: 1242 verschiedene Texte, zusammen rund 0,5 Mio.).
 */
const SPEICHER_ZEICHEN_GESAMT = 4_000_000;
const SPEICHER_ZEICHEN_JE_EINTRAG = 200_000;
const zwischenspeicher = new Map<string, string>();
let speicherZeichen = 0;

function ausSpeicher(schluessel: string): string | undefined {
  const treffer = zwischenspeicher.get(schluessel);
  if (treffer === undefined) return undefined;
  zwischenspeicher.delete(schluessel);
  zwischenspeicher.set(schluessel, treffer);
  return treffer;
}

function merke(schluessel: string, ergebnis: string): void {
  const zeichen = schluessel.length + ergebnis.length;
  if (zeichen > SPEICHER_ZEICHEN_JE_EINTRAG) return;
  zwischenspeicher.set(schluessel, ergebnis);
  speicherZeichen += zeichen;
  for (const [aeltester, wert] of zwischenspeicher) {
    if (speicherZeichen <= SPEICHER_ZEICHEN_GESAMT) break;
    zwischenspeicher.delete(aeltester);
    speicherZeichen -= aeltester.length + wert.length;
  }
}

function leereSpeicher(): void {
  zwischenspeicher.clear();
  speicherZeichen = 0;
}

let gemeldeteFehler = 0;

function melde(fehler: unknown): void {
  // Ein Filterfehler darf weder die Seite umwerfen noch das Protokoll fluten.
  if (gemeldeteFehler >= 5) return;
  gemeldeteFehler += 1;
  console.error('[html-filter] Filtern fehlgeschlagen', fehler);
}

/** Ergebnis des Filters samt Auskunft, ob wirklich gefiltert werden konnte. */
export interface GefiltertesHtml {
  html: string;
  /**
   * `false`: NOTBEHELF — es konnte nicht gefiltert werden (kein Filter, Fehler,
   * Text abgelehnt), `html` ist deshalb reiner Text ohne jedes Tag.
   */
  gefiltert: boolean;
}

/**
 * Der gemeinsame Ablauf: erst nachschlagen, sonst filtern und merken. Gibt es
 * keinen Lauf oder scheitert er, kommt das sichere Ergebnis `ohneFilter`
 * heraus — nie der ungefilterte Text und nie eine Seite, die wegen des
 * Filters nicht ausgeliefert wird.
 *
 * Auch eine ABLEHNUNG wird gemerkt (eigener Schluessel): Der Server-Filter
 * lehnt uebergrosse oder uebermaessig verschachtelte Texte ab, und das
 * Einlesen bis zur Ablehnung kostet Zeit (gemessen bis 0,3 s). Ohne Merken
 * liesse sich der Server mit jedem Abruf derselben Seite erneut beschaeftigen.
 * Fehlt der Filter ganz, wird nichts gemerkt — sobald er da ist, wird richtig
 * gefiltert.
 */
function filtere(
  art: string,
  eingabe: string,
  lauf: (() => string) | undefined,
  ohneFilter: () => string,
): GefiltertesHtml {
  const treffer = ausSpeicher(`${art}:${eingabe}`);
  if (treffer !== undefined) return { html: treffer, gefiltert: true };
  if (!lauf) return { html: ohneFilter(), gefiltert: false };
  const abgelehnt = ausSpeicher(`${art}!:${eingabe}`);
  if (abgelehnt !== undefined) return { html: abgelehnt, gefiltert: false };
  let ergebnis: string;
  try {
    ergebnis = lauf();
  } catch (fehler) {
    melde(fehler);
    const ersatz = ohneFilter();
    merke(`${art}!:${eingabe}`, ersatz);
    return { html: ersatz, gefiltert: false };
  }
  merke(`${art}:${eingabe}`, ergebnis);
  return { html: ergebnis, gefiltert: true };
}

/**
 * HTML eines Nutzers (Slide-Text, Credits, Quellen, Kontakt) fuer die Ausgabe
 * filtern. Liefert HTML, das ohne Bedenken per innerHTML oder vom Server in
 * die Seite geschrieben werden kann — und dazu, ob es der Notbehelf ist.
 */
export function filtereHtmlMitStand(
  html: string | null | undefined,
): GefiltertesHtml {
  if (!html) return { html: '', gefiltert: true };
  const lauf = htmlLauf();
  return filtere('h', html, lauf && (() => lauf(html)), () => nurText(html));
}

/** Wie `filtereHtmlMitStand`, nur das HTML. */
export function filtereHtml(html: string | null | undefined): string {
  return filtereHtmlMitStand(html).html;
}

/**
 * SVG eines Nutzers (Vektor-Ebene, SVG-Overlay) filtern, bevor es ins Dokument
 * gesetzt wird. SVG wird nur im Browser gezeichnet; ohne Dokument gibt es
 * keins — der Aufrufer zeigt dann nichts.
 */
export function filtereSvg(svg: string | null | undefined): string {
  if (!svg) return '';
  const browser = imBrowserFiltern();
  return filtere(
    's',
    svg,
    browser ? () => browser.svg.sanitize(svg, SVG_KONFIG) : undefined,
    () => '',
  ).html;
}
